// OUTPUT (io_master) machine_card fixture — the A5 hero layout (ME-CARDS-R1).
//
// Mirrors output-model.PNG: the big-number hero (parts produced), the companion
// RATE line with its explicit denominator (reject rate · 267 / 5,607), and the
// supporting tile row — waste count, the current SKU (a snapshot string), and a
// genuinely PENDING uptime tile that must render a dash with "not wired yet",
// never a fabricated 0.
//
// Also carries the `run_state` + `current_sku` snapshot figures the overview
// roll-up turns into machine-state chips.
import type { MachineCardArtifactData } from "../../../chatBot/models/machineEvidenceModels";

export const OUTPUT_HERO_MACHINE_GUID = "d72d27d9-0308-49e6-7c19-08dec02ae617";

export const OUTPUT_HERO_VALUES = {
  parts: 5607,
  rejectRatePct: 4.76,
  rejects: 267,
  waste: 102,
  sku: "WHT_007_607",
  runState: "running",
} as const;

export const OUTPUT_HERO_MACHINE_CARD: MachineCardArtifactData = {
  schema_version: 1,
  mode: "snapshot",
  machine: {
    machine: OUTPUT_HERO_MACHINE_GUID,
    machine_id_full: OUTPUT_HERO_MACHINE_GUID,
    class_template: null,
    display_name: "MASHi2601",
  },
  freshness: {
    state: "LIVE",
    as_of: "2026-08-22T07:15:00+02:00",
    last_sample_at: "2026-08-22T07:14:58+02:00",
    age_seconds: 2,
    source: "mash_liveness",
    provenance: "live",
    event_time: null,
  },
  tabs: [
    {
      part_slug: "io_master",
      name: "Output",
      parent_slug: null,
      verdict: {
        status: "on_target",
        headline: "Output nominal this shift",
        detail: null,
        basis: "occurrence count",
      },
      lane_coverage: {},
      figures: [
        {
          id: "f_part",
          label: "parts produced",
          value: OUTPUT_HERO_VALUES.parts,
          unit: null,
          window: "this_shift",
          window_start: "2026-08-22T06:00:00+02:00",
          window_end: "2026-08-22T14:00:00+02:00",
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: OUTPUT_HERO_VALUES.parts,
          source_ref: "s_part",
          freshness: null,
          breakdown: null,
        },
        {
          // The companion RATE line — a share is ALWAYS shown with its base.
          id: "f_reject_rate",
          label: "reject rate",
          value: OUTPUT_HERO_VALUES.rejectRatePct,
          unit: "%",
          window: "this_shift",
          window_start: "2026-08-22T06:00:00+02:00",
          window_end: "2026-08-22T14:00:00+02:00",
          denominator: { num: OUTPUT_HERO_VALUES.rejects, den: OUTPUT_HERO_VALUES.parts },
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: OUTPUT_HERO_VALUES.parts,
          source_ref: "s_reject",
          freshness: null,
          breakdown: null,
        },
        {
          id: "f_waste_job",
          label: "parts to waste",
          value: OUTPUT_HERO_VALUES.waste,
          unit: null,
          window: "this_shift",
          window_start: "2026-08-22T06:00:00+02:00",
          window_end: "2026-08-22T14:00:00+02:00",
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: OUTPUT_HERO_VALUES.waste,
          source_ref: "s_waste",
          freshness: null,
          breakdown: null,
        },
        {
          // SNAPSHOT string figure — also drives the overview `sku:` chip.
          id: "f_current_sku",
          label: "current product (SKU)",
          value: OUTPUT_HERO_VALUES.sku,
          unit: null,
          window: null,
          window_start: null,
          window_end: null,
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: null,
          source_ref: "s_current_sku",
          freshness: null,
          breakdown: null,
        },
        {
          // SNAPSHOT string figure — the overview `run_state:` chip.
          id: "f_run_state",
          label: "run state",
          value: OUTPUT_HERO_VALUES.runState,
          unit: null,
          window: null,
          window_start: null,
          window_end: null,
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: null,
          source_ref: "s_run_state",
          freshness: null,
          breakdown: null,
        },
        {
          // The read exists but is NOT wired — a dash + reason, never a 0.
          id: "f_uptime",
          label: "uptime",
          value: null,
          unit: null,
          window: "this_shift",
          window_start: "2026-08-22T06:00:00+02:00",
          window_end: "2026-08-22T14:00:00+02:00",
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: null,
          source_ref: null,
          freshness: null,
          breakdown: {
            pending: true,
            pending_reason: "read wiring",
            group_label: null,
            truncated: null,
            rows: [],
          },
        },
      ],
      trend: null,
      live: null,
    },
  ],
  sources: [
    { id: "s_part", signal_key: "part", window: "this_shift", window_start: "2026-08-22T06:00:00+02:00", window_end: "2026-08-22T14:00:00+02:00", count: 5607, source_grain: "event", read_source: "telemetry store", edge_path: null },
    { id: "s_reject", signal_key: "reject", window: "this_shift", window_start: "2026-08-22T06:00:00+02:00", window_end: "2026-08-22T14:00:00+02:00", count: 267, source_grain: "event", read_source: "telemetry store", edge_path: null },
    { id: "s_waste", signal_key: "waste_job", window: "this_shift", window_start: "2026-08-22T06:00:00+02:00", window_end: "2026-08-22T14:00:00+02:00", count: 102, source_grain: "event", read_source: "telemetry store", edge_path: null },
    { id: "s_current_sku", signal_key: "current_sku", window: null, window_start: null, window_end: null, count: null, source_grain: "live_snapshot", read_source: "telemetry store", edge_path: null },
    { id: "s_run_state", signal_key: "run_state", window: null, window_start: null, window_end: null, count: null, source_grain: "live_snapshot", read_source: "telemetry store", edge_path: null },
  ],
  frozen: { is_frozen: false, frozen_at: null },
};
